import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Globe, DollarSign, Users, HelpCircle, Lock, Settings } from "lucide-react";
import Header from "../components/Header";
import { Link, useParams } from 'react-router-dom';

export default function FAQ() {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  const currentLang = lang || (navigator.language?.split('-')[0] === 'it' ? 'it' : 'en');
  
  const getLocalizedPath = (path) => {
    return `/${currentLang}${path}`;
  };
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const [activeCategory, setActiveCategory] = useState("basics");
  const [openIndex, setOpenIndex] = useState(null);

  // FAQ categories with translations
  const faqCategories = [
    {
      id: "basics",
      name: t('faq.categories.basics'),
      icon: <Globe className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.basics.q1.question'),
          answer: t('faq.basics.q1.answer'),
        },
        {
          question: t('faq.basics.q2.question'),
          answer: (
            <p>
              {t('faq.basics.q2.answer').split("qui")[0]}{" "}
              <Link to={getLocalizedPath("/signup")} className="text-green-600 font-medium hover:underline">
                {t('header.signup')}
              </Link>{" "}
              {t('faq.basics.q2.answer').split("qui")[1] || ''}
            </p>
          ),
          isJSX: true
        },
        {
          question: t('faq.basics.q3.question'),
          answer: t('faq.basics.q3.answer'),
        },
        {
          question: t('faq.basics.q4.question'),
          answer: t('faq.basics.q4.answer'),
        },
        {
          question: t('faq.basics.q5.question'),
          answer: t('faq.basics.q5.answer'),
        }
      ]
    },
    {
      id: "earnings",
      name: t('faq.categories.earnings'),
      icon: <DollarSign className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.earnings.q1.question'),
          answer: [
            t('faq.earnings.q1.answer1'),
            t('faq.earnings.q1.answer2'),
            t('faq.earnings.q1.answer3'),
            t('faq.earnings.q1.answer4'),
            t('faq.earnings.q1.answer5')
          ],
        },
        {
          question: t('faq.earnings.q2.question'),
          answer: t('faq.earnings.q2.answer'),
        },
        {
          question: t('faq.earnings.q3.question'),
          answer: [
            t('faq.earnings.q3.answer1'),
            t('faq.earnings.q3.answer2'),
            t('faq.earnings.q3.answer3'),
            t('faq.earnings.q3.answer4')
          ],
        },
        {
          question: t('faq.earnings.q4.question'),
          answer: t('faq.earnings.q4.answer'),
        },
        {
          question: t('faq.earnings.q5.question'),
          answer: t('faq.earnings.q5.answer'),
        }
      ]
    },
    {
      id: "payments",
      name: t('faq.categories.payments'),
      icon: <DollarSign className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.payments.q1.question'),
          answer: t('faq.payments.q1.answer'),
        },
        {
          question: t('faq.payments.q2.question'),
          answer: t('faq.payments.q2.answer'),
        },
        {
          question: t('faq.payments.q3.question'),
          answer: t('faq.payments.q3.answer'),
        }
      ]
    },
    {
      id: "account",
      name: t('faq.categories.account'),
      icon: <Users className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.account.q1.question'),
          answer: t('faq.account.q1.answer'),
        },
        {
          question: t('faq.account.q2.question'),
          answer: t('faq.account.q2.answer'),
        },
        {
          question: t('faq.account.q3.question'),
          answer: t('faq.account.q3.answer'),
        }
      ]
    },
    {
      id: "marketing",
      name: t('faq.categories.marketing'),
      icon: <Settings className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.marketing.q1.question'),
          answer: t('faq.marketing.q1.answer'),
        },
        {
          question: t('faq.marketing.q2.question'),
          answer: t('faq.marketing.q2.answer'),
        },
        {
          question: t('faq.marketing.q3.question'),
          answer: t('faq.marketing.q3.answer'),
        },
        {
          question: t('faq.marketing.q4.question'),
          answer: t('faq.marketing.q4.answer'),
        }
      ]
    },
    {
      id: "support",
      name: t('faq.categories.support'),
      icon: <HelpCircle className="h-5 w-5" />,
      faqs: [
        {
          question: t('faq.support.q1.question'),
          answer: (
            <p>
              {t('faq.support.q1.answer').split("qui")[0]}{" "}
              <Link to={getLocalizedPath("/contact")} className="text-green-600 font-medium hover:underline">
                {t('footer.contactUs')}
              </Link>{" "}
              {t('faq.support.q1.answer').split("qui")[1] || ''}
            </p>
          ),
          isJSX: true
        }
      ]
    }
  ];

  // Function to format the answers (array or string)
  const formatAnswer = (answer) => {
    if (Array.isArray(answer)) {
      return (
        <ul className="list-disc pl-5 space-y-2 mt-3">
          {answer.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    }
    return <p className="mt-3 text-lg text-gray-600">{answer}</p>;
  };

  return (
    <div className="App min-h-screen bg-gray-50 text-custom-black">
      <Header />
      <div className="max-w-6xl mx-auto py-10 px-4">
        <h1 className="text-4xl font-bold text-center mt-4">{t('faq.title')}</h1>
        <p className="text-center text-lg text-gray-600 mt-2 mb-8">
          {t('faq.subtitle')}
        </p>

        {/* Category navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8 sticky top-0 bg-white py-3 z-10 border-b">
          {faqCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                activeCategory === category.id 
                  ? "bg-teal-600 text-white " 
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800"
              }`}
            >
              {category.icon}
              {category.name}
            </button>
          ))}
        </div>

        {/* Show only the FAQs of the active category */}
        <div className="mt-6 space-y-4">
          {faqCategories
            .find(cat => cat.id === activeCategory)
            ?.faqs.map((faq, index) => (
              <div
                key={index}
                className={`border shadow-xl rounded-xl p-6 transition-all hover:shadow-md ${
                  openIndex === index ? "border-teal-600" : "border-gray-300"
                }`}
              >
                <button
                  className="w-full flex justify-between items-center text-left text-xl font-semibold"
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                >
                  {faq.question}
                  {openIndex === index ? (
                    <ChevronDown className="text-green-600 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="text-gray-600 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (faq.isJSX ? faq.answer : formatAnswer(faq.answer))}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}